import { ActionType } from '../../../core/redux/actionType';
import { makeApiAction, makeRsaaTypes, ApiReducer, makeApiReducer } from '../../../core/api/rsaa';
import { Endpoints } from '../../../core/api/endpoints';
import { AppState } from '../../../core/redux/appState';
import { getLoading, addLoading, addError, noLoadingNoError } from '../../../core/api/module';
import * as R from 'ramda';
import { toMap } from '../../../language/arrays';


const KEY_ACTION = ActionType.DeleteRaterRequest;
const ACTIONS    = [
  ActionType.DeleteRaterRequest,
  ActionType.DeleteRaterResponse,
  ActionType.DeleteRaterError,
];


type Payload = string;


type Meta = {
  projectId: number;
  raterId: number;
};


export const deleteRaterAction = (
  projectId: number,
  participantId: number,
  raterId: number,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.DeleteRater,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify({
      projectId:     projectId,
      participantId: participantId,
      raterId:       raterId,
    }),
    types:    makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        projectId: projectId,
        raterId:   raterId,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${projectId}-${raterId}`),
  },
);


const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const projectId = action.meta.projectId;
  const raterId   = action.meta.raterId;
  const metaTag   = `${action.meta.projectId}-${action.meta.raterId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  const rater               = successState.projectRaters?.[`${projectId}`]?.[`${raterId}`];
  const level               = rater?.levelId || 0;
  const requirements        = toMap(successState.participantRequirements || [], req => `${req.surveyLevelId}`);
  const requirementToUpdate = requirements[rater!.levelId];

  const withRaterDeleted    = R.dissocPath(['projectRaters', `${projectId}`, `${raterId}`], successState) as AppState;

  // see if we need to set another approver
  let allUpdated = withRaterDeleted;
  const raters = withRaterDeleted.projectRaters?.[`${projectId}`] || {};
  const prospectiveApprover = R.find(r => r.levelId === level, Object.values(raters));
  if (rater?.approvalStatus === 1 && !!prospectiveApprover) {
    const markedApprover  = R.assoc('approvalStatus', 1, prospectiveApprover);
    allUpdated = R.assocPath(
      ['projectRaters', `${projectId}`, `${prospectiveApprover.raterId}`],
      markedApprover,
      withRaterDeleted,
    ) as AppState;
  }

  if (!requirementToUpdate) {
    return allUpdated;
  }

  const requirementsWithUpdatedCount = R.assocPath(
    [`${rater!.levelId}`, 'raterCount'],
    requirementToUpdate.raterCount - 1,
    requirements,
  );

  return R.assoc('participantRequirements', Object.values(requirementsWithUpdatedCount), allUpdated);
};


export const deleteRaterReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
